import {useMutation, UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient.ts'
import {httpScheduleAssignment} from '@features/assignment/http/assignment.http'
import {Assignment} from '@features/assignment/types.ts'
import {errorHandler} from '@utilities/helpers.ts'
import {captureException} from '@sentry/react'
import {getTimezone} from '@/dates.ts'
import {parseAxiosPromise} from '@/axiosInstance.ts'

export const useScheduleAssignment = ({
    assignmentId,
    options
}: {
    assignmentId: Assignment['id']
    options?: Omit<UseMutationOptions<Assignment, unknown, string>, 'mutationFn'>
}) => {
    const queryClient = useQueryClient()

    return useMutation({
        ...options,
        mutationFn: (selectedDate: string) =>
            parseAxiosPromise({
                axiosPromise: httpScheduleAssignment({
                    payload: {date: selectedDate, timezone: getTimezone()},
                    urlParams: {assignmentId: assignmentId},
                    params: {response_type: 'extended'}
                }),
                responseShape: Assignment,
                onZodError: captureException
            }),
        onSuccess: (data, variables, context) => {
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.CURRENT_ASSIGNMENTS]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACTIVE_ASSIGNMENTS_RANGE]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACTIVE_ASSIGNMENTS_TABLE]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ASSIGNMENTS_COUNT]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ASSIGNMENTS]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.HOME_TABLE]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.REQUIRED_ACTIONS]})
            void queryClient.setQueriesData({queryKey: [QUERY_KEYS.ASSIGNMENT, data.code]}, data)
            options?.onSuccess?.(data, variables, context)
        },
        onError: errorHandler
    })
}
